/**
 * The investor's activity log.
 *
 * Assembled from the rows they already own — see `lib/investor/activity-log.ts`
 * for why it is derived rather than read from `analytics_events` (admin-only
 * SELECT policy, and telemetry event names are not user-facing language), and
 * for the honest limit: these are facts with timestamps, not a full transition
 * history.
 *
 * Every source is a hook another screen already uses, so this adds a surface
 * rather than a query pattern.
 */

import { useMemo } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import Animated, { FadeInUp } from 'react-native-reanimated';
import {
  Inbox,
  KeyRound,
  DoorOpen,
  XCircle,
  Undo2,
  ClipboardCheck,
  History,
} from 'lucide-react-native';

import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import { AppLayout } from '@/components/AppLayout';
import { useThemeColors } from '@/lib/useThemeColors';
import { useMyDataRoomAccessRequests } from '@/lib/supabase/api';
import { useSubmissions } from '@/lib/supabase/submissions';
import { useCompletedDDReports } from '@/lib/dd/use-dd-reports';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useTrackScreen } from '@/lib/analytics';
import {
  buildInvestorActivity,
  describeActivity,
  type InvestorActivityEvent,
  type InvestorActivityKind,
} from '@/lib/investor/activity-log';
import { APP_LOCALE } from '@/lib/format';

function iconFor(kind: InvestorActivityKind) {
  switch (kind) {
    case 'deck_received':
      return Inbox;
    case 'access_requested':
      return KeyRound;
    case 'access_granted':
      return DoorOpen;
    case 'access_declined':
      return XCircle;
    case 'access_withdrawn':
      return Undo2;
    case 'dd_concluded':
      return ClipboardCheck;
  }
}

/** Absolute date and time — an activity log exists to say exactly when. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(APP_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString(APP_LOCALE, { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
}

function ActivityRow({ event, index }: { event: InvestorActivityEvent; index: number }) {
  const colors = useThemeColors();
  const router = useRouter();
  const Icon = iconFor(event.kind);

  const isNegative = event.kind === 'access_declined';
  const tint = isNegative ? colors.error : colors.primary[600];

  return (
    <Animated.View entering={FadeInUp.delay(40 + index * 25).duration(250)}>
      <AnimatedPressable
        onPress={
          event.startupId
            ? () => router.push(`/startup-detail?id=${event.startupId}`)
            : undefined
        }
        className="flex-row items-start gap-3 p-4 rounded-xl mb-2"
        style={{ backgroundColor: colors.surface.primary }}
      >
        <View
          className="w-9 h-9 rounded-full items-center justify-center shrink-0"
          style={{ backgroundColor: `${tint}14` }}
        >
          <Icon size={17} color={tint} />
        </View>
        <View className="flex-1">
          <Text
            numberOfLines={2}
            style={{ color: colors.text.primary }}
            className="text-sm font-medium"
          >
            {describeActivity(event)}
          </Text>
          <Text
            numberOfLines={1}
            style={{ color: colors.text.tertiary }}
            className="text-xs mt-0.5"
          >
            {formatWhen(event.at)}
          </Text>
        </View>
      </AnimatedPressable>
    </Animated.View>
  );
}

export default function InvestorActivityScreen() {
  useTrackScreen('InvestorActivity');
  const colors = useThemeColors();
  const router = useRouter();

  // `UserInfo.id` is already branded AuthUserId at its origin in
  // auth.tsx, so nothing here needs to assert a brand (ARCH-09).
  const storeUser = useProfileStore((s) => s.user);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const dealflow = useDealflowStore((s) => s.dealflow);

  const { data: submissions = [] } = useSubmissions(storeUser?.id);
  const { data: requests = [] } = useMyDataRoomAccessRequests(investorProfile?.id);
  const { data: reports = [] } = useCompletedDDReports(storeUser?.id);

  const events = useMemo(
    () =>
      buildInvestorActivity({
        submissions: submissions.map((s) => ({
          id: s.id,
          company_name: s.company_name,
          created_at: s.created_at,
        })),
        requests: requests.map((r) => ({
          startup_id: r.startup_id,
          status: r.status,
          created_at: r.created_at,
          responded_at: r.responded_at,
        })),
        // Names only — the pipeline is how a request's `startup_id` gets a
        // company to show against it.
        deals: dealflow.map((d) => ({
          startupId: d.startup.id,
          company: d.startup.company?.name ?? '',
        })),
        reports: reports.map((r) => ({
          id: r.id,
          startup_id: r.startupId,
          // `completedAt` is CompletedDD's name for concluded_at.
          concluded_at: r.completedAt,
        })),
      }),
    [submissions, requests, dealflow, reports],
  );

  return (
    <AppLayout title="Activity" breadcrumbs={[{ label: 'Activity' }]}>
      {/* Had the right shape already — back + title — but the control was 40pt
         (under the 44pt target) and called router.back() unguarded, so direct
         entry left a dead arrow. */}
      <ScreenFrame
        title="Activity"
        back={{ fallback: '/(investor)/investor-dashboard', label: 'Home' }}
      >

        <ScrollView
          className="flex-1 px-5"
          contentContainerStyle={{ paddingBottom: 32 }}
          showsVerticalScrollIndicator={false}
        >
          {events.length === 0 ? (
            <View className="items-center justify-center py-20">
              <History size={28} color={colors.text.tertiary} />
              <Text
                style={{ color: colors.text.primary }}
                className="text-base font-semibold mt-3 text-center"
              >
                Nothing here yet
              </Text>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-sm mt-1 text-center"
              >
                Decks you receive, access you request, and diligences you conclude will
                appear here.
              </Text>
            </View>
          ) : (
            events.map((event, i) => (
              <ActivityRow key={event.id} event={event} index={i} />
            ))
          )}
        </ScrollView>
      </ScreenFrame>
    </AppLayout>
  );
}
